import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { PropTypes } from "prop-types";
import CustomTimeStamp from "../../../../../../../../generics/TimeStamp/customTimeStamp";
import { addUnitsFromDate } from "../../../../../../../../utils/displayTime";
import { getSsHourlyPayload } from "../../../../../../../../config/reqConfig/anomalyDetection";
import TableStore from "@harbor/elements/utils/table/store";
import { getColumns } from "./sshourlyTableConfig";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import ApiService from "../../../../../../../../config/api-config";
import ADTableView from "../../../../genericComponents/ADTableView";
import { downloadAsCSVFile } from "../../../../../../../../utils/common";
import ErrorComponent from "../../../../../../../../common/ErrorComponent";
import Spinner from "../../../../../../../../common/Spinner";
import { payloadOnQueryUpdate } from "../../../../common/adUtils";
import { useI18n } from "../../../../../../../../i18n/useI18n";
import {
  AD_SINGLE_SITE_TABLE_DEFAULT_TGL,
  LOCAL_STORAGE_LABELS,
} from "../../../../../../../../utils/enums";
import { useTableSettings } from "../../../../../../../../utils/table";

let defaultSort = [["anomaly_rate_col", "desc"]];
const tableSettingsId = "ad_single_site_table";
const SSHourlyTable = (props) => {
  const { tViews } = useI18n("views");
  const navigate = useNavigate();
  const tableStoreRef = useRef(null);
  const [tableStore, setTableStore] = React.useState(null);
  const [tableLoader, setTableLoader] = React.useState();
  const [tableError, setTableError] = React.useState("");

  const openTableSettings = useCallback(() => {
    tableStoreRef.current?.setState({ settingsOpen: true });
  }, []);

  let columns = useMemo(
    () => getColumns(navigate, props.selectedSite, openTableSettings),
    [navigate, props.selectedSite, openTableSettings],
  );

  const columnsHook = useColumns({
    config: () => columns,
    order: defaultSort,
  });

  useEffect(() => {
    try {
      let tableStore = new TableStore({
        query: { pageLength: 30 },
        hooks: [columnsHook],
        fetchData: async ({ query, setSource, setCount }) => {
          let payload = getSsHourlyPayload(
            props.selectedHour,
            props.selectedSite,
          );
          payload = payloadOnQueryUpdate(query, payload);
          setTableLoader(true);
          let res = await ApiService.getADSingleSiteHourlyTable(payload);
          if (!res?.errorObject && !query.export) {
            setSource(res?.data?.data);
            setCount(res?.data?.count, false);
          } else if (query.export) {
            let resData = res?.data?.data;
            const exportColumns = tableStore
              .getState()
              .columns.filter((item) => item.prop !== "actions");
            const data = [
              exportColumns.map((item) => `"${item.name}"`).join(","),
            ];
            for (const item of resData) {
              data.push(
                exportColumns
                  .map((col) => `"${item[col.prop] ?? ""}"`)
                  .join(","),
              );
            }
            downloadAsCSVFile(
              data.join("\n"),
              tViews("tunnelSummaryHourlyData"),
            );
            delete query.export;
          } else {
            setTableError(res.errorObject.message);
          }
          tableStoreRef.current = tableStore;
          setTableStore(tableStore);
          setTableLoader(false);
        },
      });
      tableStoreRef.current = tableStore;
      useTableSettings(
        LOCAL_STORAGE_LABELS.AD_SINGLE_SITE_TABLE,
        AD_SINGLE_SITE_TABLE_DEFAULT_TGL,
        columnsHook,
        tableStore,
      );
    } catch (error) {
      setTableError(error);
    }
  }, [props.selectedHour]);

  return (
    <div className="ss-hourly-table">
      {props.selectedHour && (
        <>
          <div className="customt-timestamp">
            <CustomTimeStamp
              prevTime={props.selectedHour}
              currentTime={addUnitsFromDate(
                new Date(props.selectedHour),
                1,
                "h",
              )}
            />
          </div>
          {tableStore ? (
            <ADTableView
              tableStore={tableStore}
              header={tViews("sitesBasedAD.tunnelAnomalySummary")}
              columns={tableStore?.getState()?.columns}
              id="ad-sites-table"
              status={{ isLoading: tableLoader, error: tableError }}
              defaultSort={defaultSort}
              isPagination={true}
              entity={tViews("sitesBasedAD.adTunnels")}
              tableSettings={{
                localStorageName: LOCAL_STORAGE_LABELS.AD_SINGLE_SITE_TABLE,
                defaultToggles: AD_SINGLE_SITE_TABLE_DEFAULT_TGL,
                dataTestid: tableSettingsId,
              }}
            />
          ) : tableLoader ? (
            <Spinner />
          ) : (
            tableError && <ErrorComponent />
          )}
        </>
      )}
    </div>
  );
};

SSHourlyTable.propTypes = {
  selectedHour: PropTypes.string,
  selectedSite: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default SSHourlyTable;
